/**
 * AdultObservationReview — the review screen shown at the end of the Adult ASD
 * Behavioural Observation appointment.
 *
 * It brings the Observation review into parity with the SSI review (and the
 * ADHD app) by presenting the same clinical-decision structure:
 *
 *   • Evidence summary tables (shared with the CYP prototype). ADOS-informed
 *     coding scores open in a dialog from a button under the DSM table, the
 *     same pattern as MDT Review — they are not stacked inline.
 *   • Confidence Level
 *   • Condition(s) Referred For (Autism Spectrum Disorder)
 *   • Emerging Differentials (chips + per-condition notes)
 *   • Emerging Formulation        (amber — not in the final report)
 *   • Confidential Notes          (amber — not in the final report)
 *
 * The "Finish Observation" action lives in the parent page footer
 * (AdultAsdObservation), not in this component.
 *
 * This is a fully CONTROLLED component: every piece of review state is owned by
 * the parent (AdultAsdObservation) so it can be persisted to Firestore. The
 * component renders nothing to localStorage and holds no review state of its own
 * — it simply wires the shared autismReview panels to the parent's state and
 * callbacks. This is deliberately separate from the CYP ObservationSummary
 * (which remains a localStorage-backed prototype) so the two pathways do not
 * interfere with each other.
 */

import React, { useMemo } from 'react';
import ObservationEvidenceTables, { ObservationEmptyState } from './ObservationEvidenceTables';
import ConfidenceLevelSelector from '../../../../components/autismReview/ConfidenceLevelSelector';
import ConditionSelectionPanelTOC from '../../6_clinicalInterview/components/ConditionSelectionPanelTOC';
import NotInReportNotesPanel from '../../../../components/autismReview/NotInReportNotesPanel';
import AutismReferredForPanel from '../../../../components/autismReview/AutismReferredForPanel';
import { getAutismDifferentialConditions } from '../../../../constants/autismConditions';
import { getAdministeredTaskIds } from '../../../../utils/observationTaskAdministration';

export default function AdultObservationReview({
  // Evidence tables
  tasks,
  selectedTaskIds,
  allTaskData,
  adosData = null,
  criteriaEvidence = null,
  criteriaNotes = {},
  // Permissions
  canEdit = true,
  // Confidence
  confidenceStyle = 'ternary',
  confidence = null,
  confidenceTernary = null,
  onConfidenceChange,
  onConfidenceTernaryChange,
  // Emerging differentials (ICD-11 multi-select, ASD excluded)
  differentials = [],
  differentialsConfirmed = false,
  onToggleCondition,
  onConfirmDifferentials,
  onChangeDifferentials,
  // Emerging formulation (amber)
  emergingFormulation = [],
  onEmergingFormulationChange,
  // Confidential case notes (amber)
  confidentialNotes = [],
  onConfidentialNotesChange,
}) {
  const icdDifferentialConditions = useMemo(() => getAutismDifferentialConditions(), []);

  // The header count reflects only tasks that were actually administered — a
  // "Not administered" task contributes to no evidence and must not be counted.
  const administeredCount = useMemo(
    () => getAdministeredTaskIds(selectedTaskIds, allTaskData).length,
    [selectedTaskIds, allTaskData],
  );

  if (selectedTaskIds.length === 0) {
    return <ObservationEmptyState />;
  }

  return (
    <div className="flex-grow p-6 text-left">
      <div className="flex flex-col items-center gap-6">

        {/* Page header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Review of Behavioural Observation
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {criteriaEvidence && Object.values(criteriaEvidence).some(Boolean)
              ? 'Evidence decisions by DSM criteria from Criterion Mapping.'
              : `Evidence aggregated across ${administeredCount} task${administeredCount !== 1 ? 's' : ''} by DSM criteria.`}
          </p>
        </div>

        {/* Evidence summary tables (DSM + ADOS) */}
        <ObservationEvidenceTables
          tasks={tasks}
          selectedTaskIds={selectedTaskIds}
          allTaskData={allTaskData}
          adosData={adosData}
          criteriaEvidence={criteriaEvidence}
          criteriaNotes={criteriaNotes}
        />

        {/* Confidence Level */}
        <ConfidenceLevelSelector
          className="w-full max-w-5xl"
          confidenceStyle={confidenceStyle}
          confidence={confidence}
          confidenceTernary={confidenceTernary}
          onConfidenceChange={onConfidenceChange}
          onConfidenceTernaryChange={onConfidenceTernaryChange}
          canEdit={canEdit}
          description="Based on the Behavioural Observation, how likely is it in your clinical opinion that this person is autistic?"
        />

        {/* Condition(s) Referred For — read-only fixed label (always ASD). Shared
            compact Autism panel keeps its fonts in parity with the page. */}
        <AutismReferredForPanel className="w-full max-w-5xl" />

        {/* Emerging Differentials — ICD-11 multi-select (ASD excluded). Autism
            variant: compact fonts, substring search, and a running selected list. */}
        <div className="w-full max-w-5xl">
          <ConditionSelectionPanelTOC
            title="Emerging Differentials"
            variant="non-report"
            density="compact"
            searchable
            showSelectedBullets
            subtitle="Conditions you're actively considering as alternative explanations, co-occurring conditions, or to rule in/out at Diagnostic Decision. Capture your reasoning in the Emerging Formulation below."
            selectedOptions={differentials}
            isMultiSelect
            isConfirmed={differentialsConfirmed}
            onSelect={onToggleCondition}
            onConfirm={onConfirmDifferentials}
            onChangeChoice={onChangeDifferentials}
            conditions={icdDifferentialConditions}
            allowEmptyConfirm
            canEdit={canEdit}
          />
        </div>

        {/* Emerging Formulation (not in final report) — language mirrors the ADHD
            SSI Review; Autism uses a structured-notes box instead of a text area. */}
        <NotInReportNotesPanel
          className="w-full max-w-5xl"
          title="Emerging Formulation"
          description="Your working hypothesis about what's going on for this person and why. Capture the presenting picture, contributing factors, functional impact and strengths. Finalised at Diagnostic Decision."
          notes={emergingFormulation}
          onNotesChange={onEmergingFormulationChange}
          canEdit={canEdit}
          domainId="emergingFormulation"
          placeholder="Type a note and press Enter…"
        />

        {/* Confidential Notes (not in final report) */}
        <NotInReportNotesPanel
          className="w-full max-w-5xl"
          title="Confidential Notes"
          description="Confidential or sensitive observations for review at Diagnostic Decision."
          notes={confidentialNotes}
          onNotesChange={onConfidentialNotesChange}
          canEdit={canEdit}
          domainId="confidentialNotes"
          placeholder="Type a note and press Enter…"
        />

      </div>
    </div>
  );
}
